import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useStore from '../Store/Store'
import DownloadFiles from './DownloadFiles.jsx'
import blackCurlyBrackets from './images/bracketsCurlyBlack.svg'
import tableImage from './images/tableImage.svg'
import BaseAlert from '../Snackbar/NewImplementations/BaseAlert/BaseAlert'
import {
  useCreateDownloadMutation,
  useDownloadDataById
} from './RequestDownloads.js'
import styles from './styles.module.css'
import {
  residentialView,
  commercialView,
  transactionsView
} from './CustomViews.js'
import standardIcon from './images/arrow-up-right-from-square.svg'
import JSZip from 'jszip/dist/jszip.min.js'
import useEscapeKey from '../utils/useEscapeKey'

function DownloadModal ({ onClose, dataToDownload, queryToDownload, dataType }) {
  const history = useHistory()
  const { downloadData, isQueryLoading } = useStore()
  const [isLoadingLocalState, setIsLoadingLocalState] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [filesRemaining, setFilesRemaining] = useState(0)

  const [recordsToDownload, setRecordsToDownload] = useState('')
  const [selectedFormat, setSelectedFormat] = useState('csv')
  const [showAllFilters, setShowAllFilters] = useState(false)

  const [alert, setAlert] = useState({
    open: false,
    title: '',
    body: '',
    color: '#48B966',
    actions: []
  })

  const [dataFormat, setDataFormat] = useState('standard')

  const [filters, setFilters] = useState([])
  const [queryReadyToDownload, setQueryReadyToDownload] = useState({
    query: queryToDownload,
    format: selectedFormat,
    download: true,
    num_records: recordsToDownload,
    view: 'property_sourceURLs'
  })

  const [createDownload] = useCreateDownloadMutation(
    setAlert,
    queryToDownload,
    dataType
  )

  const downloadId =
    queryToDownload === downloadData.downloadQueryStore
      ? downloadData.download_id
      : null

  const { data: downloadByIdData } = useDownloadDataById(downloadId)

  const handleFormatChange = event => {
    const newFormat = event.target.value
    setSelectedFormat(newFormat)
    setQueryReadyToDownload(prevState => ({
      ...prevState,
      format: newFormat
    }))
  }

  const handleDataFormatChange = event => {
    const newFormat = event.target.value
    setDataFormat(newFormat)

    setQueryReadyToDownload(prev => {
      const next = { ...prev }

      switch (newFormat) {
        case 'residential':
          next.view = residentialView
          break
        case 'commercial':
          next.view = commercialView
          break
        case 'transactions':
          next.view = transactionsView
          break
        default: // “standard”
          next.view = 'property_sourceURLs'
      }

      return next
    })
  }

  const navigateToDownloads = () => {
    history.push('/download')
    localStorage.setItem('dfp/dataTypeNavigation', 'download')
  }

  const handleRecordsToDownloadChange = event => {
    const value = event.target.value
    const re = /^\d*$/

    if (re.test(value)) {
      setRecordsToDownload(value)
      const numericValue = value === '' ? 0 : parseInt(value, 10)
      setQueryReadyToDownload(prevState => ({
        ...prevState,
        num_records: numericValue
      }))
    }
  }

  function handleOnClickStartDownload () {
    if (!isQueryLoading) {
      setIsLoadingLocalState(true)
      createDownload({ query: queryReadyToDownload })
    }
  }

  const handleDownloadAllFiles = async () => {
    if (!downloadByIdData?.data?.results?.length) return

    setIsDownloading(true)
    setFilesRemaining(downloadByIdData.data.results.length)

    const zip = new JSZip()

    for (let i = 0; i < downloadByIdData.data.results.length; i++) {
      const url = downloadByIdData.data.results[i]

      try {
        const res = await fetch(url)
        const data = await res.arrayBuffer()

        const rawName = new URL(url).pathname.split('/').pop()
        const name = rawName || `file-${i + 1}`

        zip.file(name, data)
      } catch (e) {
        console.error('Failed to fetch', url, e)
      }

      setFilesRemaining(prev => prev - 1)
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })

    const link = document.createElement('a')
    link.href = URL.createObjectURL(zipBlob)
    link.download = `download-${downloadId}.zip`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)

    setIsDownloading(false)
  }

  const handleBackdropClick = event => {
    setIsVisible(false)
    setTimeout(() => {
      onClose()
    }, 250)
  }

  useEscapeKey(() => {
    setIsVisible(false)
    setTimeout(() => {
      onClose()
    }, 250)
  })

  useEffect(() => {
    const parseQueryToFilters = query => {
      const filterPattern = /([\w.]+):((?:"[^"]+"|\([^)]+\)|\[[^\]]+\]|[^ }\]]+))/g
      let match
      const parsedFilters = []

      while ((match = filterPattern.exec(query)) !== null) {
        let key = match[1]
        let value = match[2]

        if (value.startsWith('(') && value.endsWith(')')) {
          value = value
            .substring(1, value.length - 1)
            .split(' OR ')
            .map(segment => segment.replace(/^"|"$/g, ''))
            .join(' OR ')
        } else {
          value = value.replace(/^"|"$/g, '')
        }

        parsedFilters.push({ key, value })
      }

      setFilters(parsedFilters)
    }

    parseQueryToFilters(queryToDownload)
  }, [queryToDownload])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsVisible(true)
    }, 1)

    return () => clearTimeout(timeoutId)
  }, [])

  const buttonStatus = downloadByIdData?.data?.status

  return (
    <div
      className={styles.modalBackdrop}
      onClick={handleBackdropClick}
      role='dialog'
      aria-labelledby='downloadModalTitle'
      aria-describedby='downloadModalDescription'
    >
      <section
        className={styles.modalContent}
        style={{ transform: isVisible ? 'translateX(0)' : 'translateX(100%)' }}
        onClick={e => e.stopPropagation()}
      >
        <header className={styles.containerTitleDownloadData}>
          <h1 className={styles.downloadTitle}>Download data</h1>
          {buttonStatus === 'running' ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span
                style={{
                  fontSize: '10px',
                  marginRight: '16px',
                  display: 'block',
                  width: '147px'
                }}
              >
                You can close this window.
                <span>
                  All downloads are available
                  <span
                    style={{
                      paddingLeft: '5px',
                      color: '#8122B4',
                      cursor: 'pointer'
                    }}
                    onClick={navigateToDownloads}
                  >
                    here.
                  </span>
                </span>
              </span>
              <button
                className={`${styles.startDownloadButton} ${styles.disabledButton}`}
              >
                Download in progress
              </button>
            </div>
          ) : buttonStatus === 'completed' ? (
            <button
              className={`${styles.completedDownloadButton} ${
                isDownloading ? styles.disabledButton : ''
              }`}
              onClick={isDownloading ? undefined : handleDownloadAllFiles}
              disabled={isDownloading}
            >
              {isDownloading
                ? `Compressing files (${Math.max(filesRemaining, 0)}/${
                    downloadByIdData?.data?.results?.length || 0
                  } left)`
                : 'Download all files'}
            </button>
          ) : (
            <button
              disabled={recordsToDownload === '' || recordsToDownload === 0}
              onClick={
                isLoadingLocalState ? undefined : handleOnClickStartDownload
              }
              className={`${styles.startDownloadButton} 
                        ${
                          recordsToDownload === '' || recordsToDownload === 0
                            ? styles.disabledButton
                            : ''
                        }
                        ${isLoadingLocalState ? styles.loadingButton : ''}`}
            >
              {isLoadingLocalState === true
                ? 'Downloading...'
                : 'Start download'}
            </button>
          )}
        </header>
        <hr className={styles.hrStyle} />

        <div className={styles.containerTotalAvailableMatches}>
          <h2 className={styles.totalAvailableMatchesTitle}>
            Total available matches
          </h2>
          <span className={styles.totalAvailableMatchesNumber}>
            {dataToDownload?.num_found.toLocaleString('en-US') ?? (
              <span className={styles.totalAvailableMatchesNumber}>N/A</span>
            )}
          </span>
        </div>

        <hr className={styles.hrStyle} />

        <div className={styles.filtersContainer}>
          <h2 className={styles.filtersTitle}>Filters</h2>
          {filters
            .slice(0, showAllFilters ? filters.length : 5)
            .map((filter, index) => (
              <div key={index} className={styles.filtersRow}>
                <span className={styles.filterName}>{filter.key}:</span>
                <div>
                  <span className={styles.filterValue}>{filter.value}</span>
                </div>
              </div>
            ))}
          {filters.length > 5 && (
            <span
              className={styles.expandSpan}
              onClick={() => setShowAllFilters(!showAllFilters)}
            >
              {showAllFilters ? 'Show Less' : `Expand All (${filters.length})`}
            </span>
          )}
        </div>

        <hr className={styles.hrStyle} />

        <div className={styles.recordDownloadContainer}>
          <h2 className={styles.recordToDownloadTitle}>Records to download</h2>
          <input
            className={styles.recordToDownloadInput}
            type='text'
            value={recordsToDownload}
            onChange={handleRecordsToDownloadChange}
            disabled={
              isLoadingLocalState ||
              buttonStatus === 'running' ||
              buttonStatus === 'completed'
            }
          />
        </div>

        <hr className={styles.hrStyle} />

        <div className={styles.fileFormatContainer}>
          <h2 className={styles.fileFormatTitle}>File format</h2>
          <div className={styles.fileFormatInputsContainer}>
            <div className={styles.fileFormatCSVContainer}>
              <input
                className={styles.fileFormatInput}
                type='radio'
                value='csv'
                checked={selectedFormat === 'csv'}
                onChange={handleFormatChange}
                disabled={
                  isLoadingLocalState ||
                  buttonStatus === 'running' ||
                  buttonStatus === 'completed'
                }
              />
              <span className={styles.fileFormatLabelCSV} htmlFor='CSV'>
                CSV
              </span>

              <img
                src={tableImage}
                alt='tableImage'
                style={{ width: '16px', height: '16px' }}
              />
            </div>
            <div className={styles.fileFormatJSONContainer}>
              <input
                className={styles.fileFormatInput}
                type='radio'
                value='json'
                checked={selectedFormat === 'json'}
                onChange={handleFormatChange}
                disabled={
                  isLoadingLocalState ||
                  buttonStatus === 'running' ||
                  buttonStatus === 'completed'
                }
              />
              <span className={styles.fileFormatLabelCSV} htmlFor='JSON'>
                JSON
              </span>

              <img
                src={blackCurlyBrackets}
                alt='blackCurlyBrackets'
                style={{ width: '16px', height: '16px' }}
              />
            </div>
          </div>
        </div>
        <hr className={styles.hrStyle} />

        {dataType === 'property' && (
          <>
            {/* <hr className={styles.hrStyle} /> */}

            <div className={styles.dataFormatContainer}>
              <h2 className={styles.dataFormatTitle}>Data format</h2>
              <div className={styles.dataFormatInputsContainer}>
                {[
                  {
                    value: 'standard',
                    label: 'Standard',
                    desc: 'Displays all property fields, ordered by field name alphabetically.',
                    icon: standardIcon
                  },
                  {
                    value: 'residential',
                    label: 'Residential',
                    desc: 'Display fields most relevant to residential properties.',
                    icon: standardIcon
                  },
                  {
                    value: 'commercial',
                    label: 'Commercial',
                    desc: 'Displays fields most relevant to commercial properties.',
                    icon: standardIcon
                  },
                  {
                    value: 'transactions',
                    label: 'Transactions',
                    desc: 'Displays a row for each transaction on a property, along with basic property details.',
                    icon: standardIcon
                  }
                ].map(opt => (
                  <label key={opt.value} className={styles.dataFormatOption}>
                    <input
                      type='radio'
                      name='dataFormat'
                      value={opt.value}
                      checked={dataFormat === opt.value}
                      onChange={handleDataFormatChange}
                      disabled={
                        isLoadingLocalState ||
                        buttonStatus === 'running' ||
                        buttonStatus === 'completed'
                      }
                    />

                    <div>
                      <span>{opt.label}</span>
                      <small>{opt.desc}</small>
                    </div>
                    <a
                      href='https://docs.datafiniti.co/docs/portal-property-data-views'
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ alignSelf: 'flex-end' }}
                    >
                      <img
                        src={opt.icon}
                        alt={`${opt.label} icon`}
                        className={styles.dataFormatIcon}
                      />
                    </a>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        <hr className={styles.hrStyle} />

        <div className={styles.creditUsageContainer}>
          <h2 className={styles.creditUsageTitle} htmlFor='Credit Usage'>
            Credit usage
          </h2>
          <span className={styles.creditUsageKey}>
            {dataType === 'property'
              ? 'Property credits: '
              : dataType === 'business'
              ? 'Business credits: '
              : dataType === 'people'
              ? 'People credits: '
              : dataType === 'product'
              ? 'Product credits: '
              : ''}{' '}
          </span>
          <span className={styles.creditUsageKey}>
            {Math.floor(parseInt(recordsToDownload) || 0)}
          </span>
        </div>

        <hr className={styles.hrStyle} />
        {downloadByIdData ? (
          <DownloadFiles downloadByIdData={downloadByIdData} />
        ) : (
          <div className={styles.downloadSectionContainer}>
            <h2 className={styles.downloadSectionTitle}>Download</h2>
            <p className={styles.downloadSectionParagraph}>
              Not Started. Click "Start download" above.
            </p>
          </div>
        )}
      </section>
      {/* <SnackbarComponent snackData={snackMessage} /> */}

      <BaseAlert
        open={alert.open}
        title={alert.title}
        body={alert.body}
        color={alert.color}
        style={{ width: '320px' }}
        actions={alert.actions}
        onClose={() => setAlert(a => ({ ...a, open: false }))}
      />
    </div>
  )
}

export default DownloadModal
